// @flow
import React from 'react';
import Button from '@localytics/loc-components/lib/Button';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Node, { type NodeType, type NodeProps } from './Node';
import {
  deleteNode,
  deleteAllChildren,
  deleteAllExceptBranch
} from '../actions/api';
import { deselectNode } from '../actions/tree';
import {
  getAllChildNames,
  getChildNamesExludingBranch,
  isBranchedNode,
  isLeafNode
} from '../util/ApiUtils';
import {
  DELAY_TYPE,
  EVENT_LISTENER_TYPE,
  MESSAGE_TYPE
} from '../constants/ApiTypes';
import { getQueryParams } from '../util/RouterUtils';
import './stylesheets/DeleteNodeModal.scss';

type DeleteNodeModalPropTypes = {
  cancelModal: Function,
  closeModal: Function,
  node: NodeType,
  nodesList: Array<NodeProps>,
  nodes: Object,
  currentVersionId: number,
  currentOrchestrationId: number,
  currentOrgId: number,
  appGroupId: number,
  version: Object,
  dispatch: Function
};

type DeleteNodeModalStateTypes = {
  selectedDeleteType: string,
  selectedBranchToKeep: string
};

const DELETE_NODE_ONLY = 'DELETE_NODE_ONLY';
const DELETE_BRANCHES_EXCEPT_ONE = 'DELETE_BRANCHES_EXCEPT_ONE';
const DELETE_NODE_AND_ALL_CHILDREN = 'DELETE_NODE_AND_ALL_CHILDREN';
const BRANCH_DID_PATH = 'on_seen';
const BRANCH_DID_NOT_PATH = 'on_expired';

const DeleteSelector: Function = (props: {
  deleteType: string,
  branchToKeep: string,
  onSelect: Function
}) => {
  const { deleteType, branchToKeep, onSelect } = props;
  const isBranch = deleteType === DELETE_BRANCHES_EXCEPT_ONE;
  const selectorOptions = [
    {
      value: isBranch ? BRANCH_DID_PATH : DELETE_NODE_ONLY,
      text: isBranch ? '"did not" Path' : 'This node only'
    },
    {
      value: isBranch ? BRANCH_DID_NOT_PATH : DELETE_NODE_AND_ALL_CHILDREN,
      text: isBranch ? '"did" Path' : ' This node and its children'
    }
  ];

  return (
    <form
      onChange={event => {
        const { target: { value } } = event;
        onSelect(value);
      }}
    >
      {selectorOptions.map((selector, i) => (
        <label
          key={`deleteSelectorLabel${selector.value}`}
          htmlFor={`deleteRadioSelector${i}`}
        >
          <input
            id={`deleteRadioSelector${i}`}
            type="radio"
            value={selector.value}
            onChange={() => {}}
            checked={
              branchToKeep === selector.value || deleteType === selector.value
            }
          />
          {selector.text}
        </label>
      ))}
    </form>
  );
};

const ListOfDeletedNodes: Function = (props: {
  nodeNames: Array<string>,
  allNodes: Array<NodeType>,
  className: string
}) => {
  const { nodeNames, allNodes, className } = props;
  const relevantTypes = [DELAY_TYPE, EVENT_LISTENER_TYPE, MESSAGE_TYPE];
  const verticalSpacer = 5;
  const dialogWidth = 534;
  const relevantNodes = allNodes.filter(node => {
    const { data: { name, node_type } } = node;
    return (
      nodeNames.indexOf(name) > -1 && relevantTypes.indexOf(node_type) > -1
    );
  });
  return relevantNodes.map(nodeObj => {
    const { data: { height } } = nodeObj;
    let yPos = 0;
    const nodeData = {
      ...nodeObj,
      y: yPos,
      x: dialogWidth / 2,
      data: {
        ...nodeObj.data,
        selected: false,
        width: dialogWidth
      }
    };
    yPos += height + verticalSpacer;
    return (
      <svg
        key={`deleteNode${nodeObj.data.id}`}
        className={className}
        width={dialogWidth + 1}
        height={height + 1}
      >
        <Node node={nodeData} />
      </svg>
    );
  });
};

class DeleteNodeModal extends React.Component<
  DeleteNodeModalPropTypes,
  DeleteNodeModalStateTypes
> {
  constructor(props: DeleteNodeModalPropTypes) {
    super(props);
    this.state = {
      selectedDeleteType: this.getDeleteOperationType(),
      selectedBranchToKeep: BRANCH_DID_PATH
    };
  }

  getDeleteOperationType: Function = () => {
    const { nodes, node } = this.props;
    const { data: { name } } = node;
    if (isLeafNode(nodes, name)) {
      return DELETE_NODE_ONLY;
    } else if (isBranchedNode(node)) {
      return DELETE_BRANCHES_EXCEPT_ONE;
    }
    return DELETE_NODE_AND_ALL_CHILDREN;
  };

  getNodeNamesToDelete: Function = () => {
    const { selectedDeleteType, selectedBranchToKeep } = this.state;
    const { node, nodes } = this.props;
    const { data: { name: nodeName } } = node;
    switch (selectedDeleteType) {
      case DELETE_NODE_AND_ALL_CHILDREN:
        return getAllChildNames(nodes, nodeName);
      case DELETE_NODE_ONLY:
        return [nodeName];
      case DELETE_BRANCHES_EXCEPT_ONE:
        return getChildNamesExludingBranch(
          nodes,
          nodeName,
          selectedBranchToKeep
        );
      default:
        return [];
    }
  };

  getDisplayName: Function = node => {
    const { data: { node_type: nodeType, display_name: displayName } } = node;
    if (displayName) {
      return displayName;
    }
    switch (nodeType) {
      case EVENT_LISTENER_TYPE:
        return 'Event Listener';
      case DELAY_TYPE:
        return 'Wait Timer';
      case MESSAGE_TYPE:
        return 'Message';
      default:
        return 'Node';
    }
  };

  changeDeleteType: Function = value => {
    if (this.state.selectedDeleteType === DELETE_BRANCHES_EXCEPT_ONE) {
      this.setState({ selectedBranchToKeep: value });
    } else {
      this.setState({ selectedDeleteType: value });
    }
  };

  submitForm: Function = () => {
    const { selectedDeleteType, selectedBranchToKeep } = this.state;
    const {
      node,
      currentVersionId,
      currentOrchestrationId,
      currentOrgId,
      appGroupId,
      dispatch,
      version
    } = this.props;
    const { data: { name: nodeName } } = node;
    switch (selectedDeleteType) {
      case DELETE_NODE_AND_ALL_CHILDREN:
        dispatch(
          deleteAllChildren({
            version,
            nodeName,
            apps: version.apps,
            versionId: currentVersionId,
            orchestrationId: currentOrchestrationId,
            orgId: currentOrgId,
            appGroupId
          })
        );
        break;
      case DELETE_NODE_ONLY:
        dispatch(
          deleteNode({
            version,
            nodeName,
            apps: version.apps,
            versionId: currentVersionId,
            orchestrationId: currentOrchestrationId,
            orgId: currentOrgId,
            appGroupId
          })
        );
        break;
      case DELETE_BRANCHES_EXCEPT_ONE:
        dispatch(
          deleteAllExceptBranch({
            version,
            nodeName,
            apps: version.apps,
            branchNameToKeep: selectedBranchToKeep,
            versionId: currentVersionId,
            orchestrationId: currentOrchestrationId,
            orgId: currentOrgId,
            appGroupId
          })
        );
        break;
      default:
    }
    this.props.dispatch(deselectNode());
  };

  render() {
    const { cancelModal, closeModal, node, nodes, nodesList } = this.props;
    const { selectedDeleteType, selectedBranchToKeep } = this.state;
    const { data: { name } } = node;
    const namesOfDeletedNodes = this.getNodeNamesToDelete();
    const nameToDisplay = this.getDisplayName(node);
    return (
      <div>
        <div className="DeleteNodeModal__Title">
          <span>Confirm Delete</span>
          <button className="DelayNodeModal__CloseButton" onClick={closeModal}>
            &times;
          </button>
        </div>
        <div className="DeleteNodeModal__Content">
          <div className="DeleteNodeModal__MainText">
            <span className="DeleteNodeModal__MainText__ConfirmMessage">
              Are you sure you want to permanently delete{' '}
              <span style={{ fontWeight: 'bold' }}>{nameToDisplay}</span>?
            </span>
          </div>

          {!isLeafNode(nodes, name) && (
            <div className="DeleteNodeModal__ChildList">
              <span className="DeleteNodeModal__ChildList__ConfirmMessage">
                Please confirm which path to delete:
              </span>
              <div className="DeleteNodeModal__ChildList__BranchSelector">
                <DeleteSelector
                  deleteType={selectedDeleteType}
                  branchToKeep={selectedBranchToKeep}
                  onSelect={this.changeDeleteType}
                />
              </div>
              <div className="DeleteNodeModal__ChildList__NodesList">
                <span>The following nodes will be permanently deleted:</span>
                <ListOfDeletedNodes
                  nodeNames={namesOfDeletedNodes}
                  allNodes={nodesList}
                  className="DeleteNodeModal__ChildList__NodesList__ChildNode"
                />
              </div>
            </div>
          )}
        </div>

        <div className="DeleteNodeModal__ControlButtons">
          <div>
            <Button onClick={this.submitForm}>Confirm Delete</Button>
            <Button type="primary" onClick={cancelModal}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    orgId: currentOrgId,
    orchestrationId: currentOrchestrationId,
    versionId: currentVersionId
  } = getQueryParams(ownProps.location);
  const {
    api: { versionsByOrchestrationId },
    tree: { nodesList, selectedNode }
  } = state;
  const version =
    versionsByOrchestrationId[currentOrchestrationId][currentVersionId];
  const { nodes, app_group_id } = version;
  return {
    nodes,
    nodesList,
    node: nodesList.find(n => n.data.name === selectedNode),
    version,
    currentVersionId,
    versionsByOrchestrationId,
    currentOrgId,
    appGroupId: app_group_id,
    currentOrchestrationId
  };
};

export default withRouter(connect(mapStateToProps)(DeleteNodeModal));



// WEBPACK FOOTER //
// ./src/components/DeleteNodeModal.jsx